/**
 * Public vendor (shop.vendors) service.
 *
 * Shop is platform-scope, NOT tenant-scope: there is no `org_id`. Every
 * list service here paginates with keyset (per ai-rules/pagination-required.md)
 * but does not require an ActiveContext.
 *
 * Surfaces using this service:
 *   * apps/shop  -- public browse (anon or signed-in buyer)
 *   * apps/sell  -- vendor self-management (vendor team only)
 *   * apps/web /shop module -- in-app browse for paid operators
 *   * apps/admin -- platform admin moderation
 */

import { supabase, supabaseEndpoint } from '../client';
import { sendEmail } from './email';
import {
  applyKeyset,
  buildNextCursor,
  clampPageSize,
  selectCountOption,
  type KeysetInput,
  type ListResult,
} from '../pagination';
import type {
  ShopVendor,
  ShopVendorInsert,
  ShopVendorUpdate,
  ShopVendorPublic,
  ShopVendorStatus,
  ShopVendorUser,
  ShopVendorUserRole,
  ShopVendorTeamMember,
  ShopVendorInvite,
} from './shopTypes';

// =========================================================================
// Vendors -- list / get
// =========================================================================

export interface ListShopVendorsInput extends KeysetInput {
  /** Limit to vendors matching a free-text search across company_name. */
  search?: string;
  /** Filter on a specific status. Default behavior depends on caller. */
  status?: ShopVendorStatus;
  /** Filter on inventory country (ISO2). */
  inventoryCountry?: string;
  /** Filter on at least one of these ships-to countries (ISO2). */
  shipsToCountry?: string;
  /** Include archived rows (admin only). */
  includeArchived?: boolean;
  /** Featured-first ordering (featured_until desc nulls last, ad_rank desc, updated_at desc). */
  featuredFirst?: boolean;
}

/**
 * List approved vendors for the public directory.
 * Anon + authenticated callers see only `status='approved'` rows via RLS.
 */
export async function listShopVendorsPublic(
  input: Omit<ListShopVendorsInput, 'status' | 'includeArchived'> = {},
): Promise<ListResult<ShopVendorPublic>> {
  const pageSize = clampPageSize(input.pageSize);
  let q = (supabase() as any)
    .schema('shop')
    .from('v_vendor_public')
    .select('*', selectCountOption(input.countMode));

  if (input.inventoryCountry) q = q.eq('inventory_country_iso2', input.inventoryCountry);
  if (input.shipsToCountry) q = q.contains('ships_to_country_iso2', [input.shipsToCountry]);
  if (input.search && input.search.trim().length > 0) {
    const s = `%${input.search.trim()}%`;
    q = q.or([`company_name.ilike.${s}`, `slug.ilike.${s}`].join(','));
  }

  if (input.featuredFirst) {
    q = q
      .order('featured_until', { ascending: false, nullsFirst: false })
      .order('ad_rank', { ascending: false })
      .order('updated_at', { ascending: false })
      .order('id', { ascending: false });
  } else {
    q = applyKeyset(q, input.cursor, 'updated_at');
  }
  q = q.limit(pageSize);

  const { data, error, count } = await q;
  if (error) throw error;
  const rows = (data ?? []) as ShopVendorPublic[];
  return {
    rows,
    nextCursor: input.featuredFirst
      ? null
      : buildNextCursor(rows as unknown as Array<Record<string, unknown>>, pageSize, 'updated_at'),
    total: typeof count === 'number' ? count : null,
    totalIsExact: input.countMode === 'exact',
  };
}

/**
 * Admin / vendor-team list. Returns full ShopVendor rows including
 * pending/suspended statuses (visible to admins; vendor users see only
 * their own row via RLS).
 */
export async function listShopVendors(
  input: ListShopVendorsInput = {},
): Promise<ListResult<ShopVendor>> {
  const pageSize = clampPageSize(input.pageSize);
  let q = (supabase() as any)
    .schema('shop')
    .from('vendors')
    .select('*', selectCountOption(input.countMode));

  if (!input.includeArchived) q = q.is('archived_at', null);
  if (input.status) q = q.eq('status', input.status);
  if (input.inventoryCountry) q = q.eq('inventory_country_iso2', input.inventoryCountry);
  if (input.shipsToCountry) q = q.contains('ships_to_country_iso2', [input.shipsToCountry]);
  if (input.search && input.search.trim().length > 0) {
    const s = `%${input.search.trim()}%`;
    q = q.or(
      [
        `company_name.ilike.${s}`,
        `slug.ilike.${s}`,
        `support_email.ilike.${s}`,
      ].join(','),
    );
  }

  q = applyKeyset(q, input.cursor, 'updated_at');
  q = q.limit(pageSize);

  const { data, error, count } = await q;
  if (error) throw error;
  const rows = (data ?? []) as ShopVendor[];
  return {
    rows,
    nextCursor: buildNextCursor(
      rows as unknown as Array<Record<string, unknown>>,
      pageSize,
      'updated_at',
    ),
    total: typeof count === 'number' ? count : null,
    totalIsExact: input.countMode === 'exact',
  };
}

/**
 * Batch-resolve public vendor rows for inbox thread labels.
 *
 * Bounded fan-out: caller passes a list of ids it already has; we cap the
 * returned set at `BATCH_BY_IDS_HARD_CAP` so an accidentally-unbounded
 * upstream collection (e.g. a future infinite-scroll thread feed) can't
 * silently trip PostgREST's `max_rows` cutoff. Per
 * `ai-rules/pagination-required.md`, every read chain needs an explicit
 * terminal; callers that genuinely need more than the cap must page their
 * id list themselves.
 */
const BATCH_BY_IDS_HARD_CAP = 200;

export async function listShopVendorsPublicByIds(
  ids: string[],
): Promise<ShopVendorPublic[]> {
  const unique = [...new Set(ids.filter(Boolean))];
  if (unique.length === 0) return [];
  const bounded = unique.slice(0, BATCH_BY_IDS_HARD_CAP);
  const { data, error } = await (supabase() as any)
    .schema('shop')
    .from('v_vendor_public')
    .select('*')
    .in('id', bounded)
    .limit(BATCH_BY_IDS_HARD_CAP);
  if (error) throw error;
  return (data ?? []) as ShopVendorPublic[];
}

export async function getShopVendorPublicBySlug(
  slug: string,
): Promise<ShopVendorPublic | null> {
  const { data, error } = await (supabase() as any)
    .schema('shop')
    .from('v_vendor_public')
    .select('*')
    .eq('slug', slug)
    .maybeSingle();
  if (error) throw error;
  return (data ?? null) as ShopVendorPublic | null;
}

export async function getShopVendor(id: string): Promise<ShopVendor | null> {
  const { data, error } = await (supabase() as any)
    .schema('shop')
    .from('vendors')
    .select('*')
    .eq('id', id)
    .maybeSingle();
  if (error) throw error;
  return (data ?? null) as ShopVendor | null;
}

export async function getShopVendorBySlug(slug: string): Promise<ShopVendor | null> {
  const { data, error } = await (supabase() as any)
    .schema('shop')
    .from('vendors')
    .select('*')
    .eq('slug', slug)
    .maybeSingle();
  if (error) throw error;
  return (data ?? null) as ShopVendor | null;
}

// =========================================================================
// Vendor -- create / update / archive
// =========================================================================

export interface SubmitShopVendorApplicationInput {
  slug: string;
  companyName: string;
  /**
   * Optional. Used by buyers to reach the storefront's team and by
   * PeptideClients for application-received notifications. Falls through
   * to the signed-in user's auth email if omitted.
   */
  supportEmail?: string;
  contactName?: string;
  tagline?: string;
  websiteUrl?: string;
}

export interface SubmitShopVendorApplicationResult {
  vendorId: string;
  slug: string;
}

/**
 * Atomically create a pending vendor + owner membership for the signed-in user.
 * Prefers the `shop-vendor-signup` Edge Function (confirmation emails); falls
 * back to `shop.rpc_vendor_signup` when the function is unreachable.
 */
export async function submitShopVendorApplication(
  input: SubmitShopVendorApplicationInput,
): Promise<SubmitShopVendorApplicationResult> {
  const slug = input.slug.toLowerCase();
  const { data: sess } = await supabase().auth.getSession();
  const accessToken = sess.session?.access_token;
  if (!accessToken) {
    throw new Error('You must be signed in to submit a vendor application.');
  }

  const endpoint = supabaseEndpoint();
  try {
    const resp = await fetch(`${endpoint.url}/functions/v1/shop-vendor-signup`, {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        apikey: endpoint.anonKey,
        authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({
        slug,
        company_name: input.companyName,
        support_email: input.supportEmail ?? undefined,
        contact_name: input.contactName ?? undefined,
        website_url: input.websiteUrl ?? undefined,
        tagline: input.tagline ?? undefined,
      }),
    });
    const payload = (await resp.json()) as {
      ok?: boolean;
      vendor_id?: string;
      slug?: string;
      error?: string;
    };
    if (resp.ok && payload.ok && payload.vendor_id && payload.slug) {
      return { vendorId: payload.vendor_id, slug: payload.slug };
    }
    if (resp.status !== 404 && resp.status !== 502 && resp.status !== 503) {
      throw new Error(payload.error ?? `Vendor signup failed (${resp.status})`);
    }
  } catch (e) {
    if (e instanceof Error && !/fetch|network|failed/i.test(e.message)) {
      throw e;
    }
  }

  const { data, error } = await (supabase() as any)
    .schema('shop')
    .rpc('rpc_vendor_signup', {
      p_slug: slug,
      p_company_name: input.companyName,
      p_support_email: input.supportEmail ?? null,
      p_website_url: input.websiteUrl ?? null,
      p_tagline: input.tagline ?? null,
    });
  if (error) throw error;
  const vendor = data as ShopVendor;
  return { vendorId: vendor.id, slug: vendor.slug };
}

export interface CreateShopVendorInput {
  slug: string;
  companyName: string;
  supportEmail?: string;
  tagline?: string;
  websiteUrl?: string;
}

/**
 * Create a vendor row. Typically called by the shop-vendor-signup Edge
 * Function under service_role so it can write the row, the membership,
 * AND the matching auth user atomically.
 */
export async function createShopVendor(input: CreateShopVendorInput): Promise<ShopVendor> {
  const row: ShopVendorInsert = {
    slug: input.slug.toLowerCase(),
    company_name: input.companyName,
    support_email: input.supportEmail ?? undefined,
    tagline: input.tagline ?? undefined,
    website_url: input.websiteUrl ?? undefined,
    status: 'pending',
  };
  const { data, error } = await (supabase() as any)
    .schema('shop')
    .from('vendors')
    .insert(row)
    .select('*')
    .single();
  if (error) throw error;
  return data as ShopVendor;
}

export interface UpdateShopVendorInput {
  id: string;
  companyName?: string;
  tagline?: string | null;
  descriptionMd?: string | null;
  logoUrl?: string | null;
  bannerUrl?: string | null;
  websiteUrl?: string | null;
  supportEmail?: string | null;
  supportPhone?: string | null;
  /**
   * Optional vendor WhatsApp contact. Treated as a separate
   * profile-completion step from `supportPhone` by
   * `shop.rpc_vendor_profile_completion`.
   */
  supportWhatsapp?: string | null;
  inventoryCountryIso2?: string | null;
  shipsToCountryIso2?: string[];
  paymentMethods?: ShopVendor['payment_methods'];
  /** Public storefront fields (see vendor_public_storefront migration). */
  publicStorefrontEnabled?: boolean;
  publicHeadline?: string | null;
  publicAboutMd?: string | null;
  publicLogoUrl?: string | null;
}

export async function updateShopVendor(
  input: UpdateShopVendorInput,
): Promise<ShopVendor> {
  const patch: ShopVendorUpdate = {};
  if (input.companyName !== undefined) patch.company_name = input.companyName;
  if (input.tagline !== undefined) patch.tagline = input.tagline;
  if (input.descriptionMd !== undefined) patch.description_md = input.descriptionMd;
  if (input.logoUrl !== undefined) patch.logo_url = input.logoUrl;
  if (input.bannerUrl !== undefined) patch.banner_url = input.bannerUrl;
  if (input.websiteUrl !== undefined) patch.website_url = input.websiteUrl;
  if (input.supportEmail !== undefined) patch.support_email = input.supportEmail;
  if (input.supportPhone !== undefined) patch.support_phone = input.supportPhone;
  if (input.supportWhatsapp !== undefined) patch.support_whatsapp = input.supportWhatsapp;
  if (input.inventoryCountryIso2 !== undefined)
    patch.inventory_country_iso2 = input.inventoryCountryIso2;
  if (input.shipsToCountryIso2 !== undefined)
    patch.ships_to_country_iso2 = input.shipsToCountryIso2;
  if (input.paymentMethods !== undefined) patch.payment_methods = input.paymentMethods;
  if (input.publicStorefrontEnabled !== undefined)
    patch.public_storefront_enabled = input.publicStorefrontEnabled;
  if (input.publicHeadline !== undefined) patch.public_headline = input.publicHeadline;
  if (input.publicAboutMd !== undefined) patch.public_about_md = input.publicAboutMd;
  if (input.publicLogoUrl !== undefined) patch.public_logo_url = input.publicLogoUrl;

  const { data, error } = await (supabase() as any)
    .schema('shop')
    .from('vendors')
    .update(patch)
    .eq('id', input.id)
    .select('*')
    .single();
  if (error) throw error;
  return data as ShopVendor;
}

// =========================================================================
// Public storefront analytics (sell-app share card sparkline)
// =========================================================================

export interface StorefrontViewStatRow {
  /** ISO date (YYYY-MM-DD). */
  day: string;
  view_count: number;
}

/**
 * Returns a 30-day zero-filled daily series of public storefront views
 * for the given vendor. Backed by app_public.get_storefront_view_stats
 * which gates on shop.vendor_users membership.
 */
export async function getStorefrontViewStats(
  vendorId: string,
): Promise<StorefrontViewStatRow[]> {
  const { data, error } = await (supabase() as any)
    .schema('app_public')
    .rpc('get_storefront_view_stats', { p_vendor_id: vendorId });
  if (error) throw error;
  return (data ?? []) as StorefrontViewStatRow[];
}

// =========================================================================
// Admin RPCs (status transitions + audit-logged)
// =========================================================================

/**
 * Platform-admin status transition. Calls a SECURITY DEFINER RPC so the
 * audit_log row carries the admin's actor_id and an explicit reason.
 *
 * The RPC is shipped in a follow-on migration (admin-moderation step).
 * Service signature kept here so callers can compile against it.
 */
export async function adminTransitionShopVendor(input: {
  vendorId: string;
  toStatus: ShopVendorStatus;
  reason?: string;
}): Promise<ShopVendor> {
  const { data, error } = await (supabase() as any)
    .schema('shop')
    .rpc('rpc_admin_transition_vendor', {
      p_vendor_id: input.vendorId,
      p_to_status: input.toStatus,
      p_reason: input.reason ?? null,
    });
  if (error) throw error;
  const vendor = data as ShopVendor;

  if (input.toStatus === 'approved') {
    await maybeSendVendorApprovedEmail(vendor);
  }

  return vendor;
}

async function maybeSendVendorApprovedEmail(vendor: ShopVendor): Promise<void> {
  const toEmail = (vendor.support_email ?? '').trim();
  if (!toEmail) return;

  await sendEmail({
    templateKey: 'shop_vendor_approved',
    to: toEmail,
    variables: {
      contact_name: vendor.company_name,
      company_name: vendor.company_name,
      public_profile_url: `https://shop.peptideclients.com/@${vendor.slug}`,
      dashboard_url: 'https://sell.peptideclients.com/',
      vendor_id: vendor.id,
    },
  });
}

/**
 * Platform-admin suspend action with strict reason validation.
 * Backed by `shop.rpc_admin_suspend_vendor` which enforces a 4..500-char
 * trimmed reason and writes suspension-specific audit metadata.
 */
export async function adminSuspendShopVendor(input: {
  vendorId: string;
  reason: string;
}): Promise<ShopVendor> {
  const { data, error } = await (supabase() as any)
    .schema('shop')
    .rpc('rpc_admin_suspend_vendor', {
      p_vendor_id: input.vendorId,
      p_reason: input.reason,
    });
  if (error) throw error;
  return data as ShopVendor;
}

/**
 * Platform-admin unsuspend action.
 * Backed by `shop.rpc_admin_unsuspend_vendor`, which records
 * `unsuspended_at`/`unsuspended_by_admin_id` and emits audit events.
 */
export async function adminUnsuspendShopVendor(input: {
  vendorId: string;
}): Promise<ShopVendor> {
  const { data, error } = await (supabase() as any)
    .schema('shop')
    .rpc('rpc_admin_unsuspend_vendor', {
      p_vendor_id: input.vendorId,
    });
  if (error) throw error;
  return data as ShopVendor;
}

// =========================================================================
// Vendor users (team) + invites
// =========================================================================

export async function listShopVendorUsers(vendorId: string): Promise<ShopVendorUser[]> {
  const { data, error } = await (supabase() as any)
    .schema('shop')
    .from('vendor_users')
    .select('*')
    .eq('vendor_id', vendorId)
    .is('archived_at', null)
    .order('created_at', { ascending: true })
    .limit(200);
  if (error) throw error;
  return (data ?? []) as ShopVendorUser[];
}

/**
 * List the vendor's teammates with the identity columns the UI needs
 * (email + display_name). PostgREST can't read auth.users from the
 * authenticated role, so this calls `shop.rpc_vendor_team_list`, a
 * SECURITY DEFINER RPC that gates on the vendor owner/admin role.
 *
 * Use this on the sell "Team access" page instead of
 * `listShopVendorUsers` (which only returns user_id UUIDs).
 */
export async function listShopVendorTeam(vendorId: string): Promise<ShopVendorTeamMember[]> {
  const { data, error } = await (supabase() as any)
    .schema('shop')
    .rpc('rpc_vendor_team_list', { p_vendor_id: vendorId });
  if (error) throw error;
  return (data ?? []) as ShopVendorTeamMember[];
}

export async function setShopVendorUserRole(input: {
  vendorId: string;
  userId: string;
  role: ShopVendorUserRole;
}): Promise<void> {
  const { error } = await (supabase() as any)
    .schema('shop')
    .from('vendor_users')
    .update({ role: input.role })
    .eq('vendor_id', input.vendorId)
    .eq('user_id', input.userId);
  if (error) throw error;
}

export async function removeShopVendorUser(input: {
  vendorId: string;
  userId: string;
}): Promise<void> {
  const { error } = await (supabase() as any)
    .schema('shop')
    .from('vendor_users')
    .update({
      archived_at: new Date().toISOString(),
    })
    .eq('vendor_id', input.vendorId)
    .eq('user_id', input.userId);
  if (error) throw error;
}

export async function listShopVendorInvites(
  vendorId: string,
): Promise<ShopVendorInvite[]> {
  const { data, error } = await (supabase() as any)
    .schema('shop')
    .from('vendor_invites')
    .select('*')
    .eq('vendor_id', vendorId)
    .is('accepted_at', null)
    .is('revoked_at', null)
    .order('created_at', { ascending: false })
    .limit(200);
  if (error) throw error;
  return (data ?? []) as ShopVendorInvite[];
}

export interface CreateShopVendorInviteInput {
  vendorId: string;
  email: string;
  role?: ShopVendorUserRole;
}

/**
 * Creates a pending invite. The token is generated by the
 * `rpc_shop_invite_vendor_user` RPC which also stores the hashed token
 * and emits the invite email.
 */
export async function createShopVendorInvite(
  input: CreateShopVendorInviteInput,
): Promise<{ invite_id: string; accept_url: string }> {
  const { data, error } = await (supabase() as any)
    .schema('shop')
    .rpc('rpc_invite_vendor_user', {
      p_vendor_id: input.vendorId,
      p_email: input.email,
      p_role: input.role ?? 'staff',
    });
  if (error) throw error;
  return data as { invite_id: string; accept_url: string };
}

export async function revokeShopVendorInvite(inviteId: string): Promise<void> {
  const { error } = await (supabase() as any)
    .schema('shop')
    .from('vendor_invites')
    .update({ revoked_at: new Date().toISOString() })
    .eq('id', inviteId);
  if (error) throw error;
}

// =========================================================================
// Admin: vendor owner + sessions overview
// =========================================================================
//
// Powers the "Owner & sessions" card on the admin vendor detail page
// (apps/admin/src/pages/ShopVendorDetailPage.tsx). Wraps the
// SECURITY DEFINER RPC `shop.rpc_admin_get_vendor_owner_overview`
// (migration `20260605130000_shop_admin_vendor_owner_overview.sql`).
//
// Resolves the human who owns the storefront via a two-step fallback:
//   1. `shop.vendors.claimed_by_user_id` (set by the claim-flow RPC)
//   2. earliest accepted `role='owner'` row on `shop.vendor_users`
//
// Returns the owner's identity (auth + profile + phone + role columns),
// last login + last activity timestamps + the most recent 10 session
// events from `core.session_audit`, and the active team headcount.
//
// Platform-admin-only. Surfaces sensitive forensic data (login email,
// IP, geo, user agent) so the gate must stay tight; the rest of this
// service file stays scoped to vendor team membership reads.

export interface AdminVendorOwnerRecentLogin {
  at: string;
  kind:
    | 'login_success'
    | 'login_failure'
    | 'logout'
    | 'mfa_challenge_success'
    | 'mfa_challenge_failure';
  success: boolean;
  failure_reason: string | null;
  ip: string | null;
  user_agent: string | null;
  geo_country: string | null;
  geo_region: string | null;
  geo_city: string | null;
  provider: string | null;
  mfa_method: string | null;
}

export interface AdminVendorOwnerOtherOwner {
  user_id: string;
  email: string | null;
  display_name: string | null;
  role: ShopVendorUserRole;
  accepted_at: string | null;
  created_at: string;
}

export interface AdminVendorOwner {
  user_id: string;
  email: string | null;
  email_confirmed_at: string | null;
  display_name: string | null;
  last_sign_in_at: string | null;
  created_at: string;
  is_platform_admin: boolean;
  archived_at: string | null;
  phone: string | null;
  phone_verified_at: string | null;
  /**
   * The owner's row on `shop.vendor_users`. `role` is null only for
   * the edge case where `shop.vendors.claimed_by_user_id` points at a
   * user whose vendor_users membership has since been archived.
   */
  role: ShopVendorUserRole | null;
  invited_by_user_id: string | null;
  invited_at: string | null;
  accepted_at: string | null;
  team_row_created_at: string | null;
}

export interface AdminVendorOwnerOverview {
  owner: AdminVendorOwner | null;
  /**
   * `'claim'`  — owner resolved from `shop.vendors.claimed_by_user_id`.
   * `'team'`   — owner resolved from the earliest accepted role=owner
   *              membership (legacy self-signup vendors).
   * `null`     — no owner attached yet (admin-created placeholder).
   */
  role_source: 'claim' | 'team' | null;
  other_owners: AdminVendorOwnerOtherOwner[];
  /** Most recent `login_success` event in `core.session_audit`. */
  last_login_at: string | null;
  last_login_ip: string | null;
  last_login_user_agent: string | null;
  last_login_geo_country: string | null;
  last_login_geo_region: string | null;
  last_login_geo_city: string | null;
  last_login_provider: string | null;
  last_login_mfa_method: string | null;
  /**
   * Most recent of `{login_success, token_refresh, mfa_challenge_success}`.
   * `token_refresh` is the best in-repo proxy for "last seen" — it
   * fires every ~1h while any tab is alive. May lag if the
   * auth-audit-hook is not capturing refreshes for this user; the
   * operator can fall back to `owner.last_sign_in_at` (Supabase Auth's
   * own field) for a coarser signal.
   */
  last_activity_at: string | null;
  recent_logins: AdminVendorOwnerRecentLogin[];
  active_team_count: number;
}

/**
 * Fetches the resolved owner + login forensics block for a vendor.
 * Platform-admin only; non-admins get `insufficient_privilege` (42501)
 * and unknown vendor ids get `vendor_not_found` (P0002).
 */
export async function adminGetShopVendorOwnerOverview(
  vendorId: string,
): Promise<AdminVendorOwnerOverview> {
  const { data, error } = await (supabase() as any)
    .schema('shop')
    .rpc('rpc_admin_get_vendor_owner_overview', { p_vendor_id: vendorId });
  if (error) throw error;
  return data as AdminVendorOwnerOverview;
}

// =========================================================================
// Vendor suspension lockdown (Feature: vendor-suspension-lockdown)
// =========================================================================
//
// Thin wrapper around `shop.rpc_get_my_vendor_suspension_state` (added
// in `20260525090000_shop_vendor_suspension_lockdown.sql`). Returns the
// caller's vendor suspension fields if any; used by the apps/sell
// `useVendorSuspensionStatus` hook to drive the chrome lockdown + the
// `<SuspendedGate>` wrappers on editable pages.

export interface VendorSuspensionState {
  vendor_id: string;
  status: string;
  suspended_at: string | null;
  suspended_reason: string | null;
}

/**
 * Returns the caller's vendor suspension state. The RPC is
 * `SECURITY INVOKER`, so RLS scopes the result to the user's own
 * vendor membership rows. Currently the apps/sell shell only handles
 * the single-vendor case (the rest of the multi-vendor switcher lives
 * in `StorefrontPickerPage`); we surface the first row that comes
 * back, which mirrors `VendorContext.vendor`.
 */
export async function getMyVendorSuspensionState(): Promise<VendorSuspensionState | null> {
  const { data, error } = await (supabase() as any)
    .schema('shop')
    .rpc('rpc_get_my_vendor_suspension_state');
  if (error) throw error;
  const rows = (data ?? []) as VendorSuspensionState[];
  return rows[0] ?? null;
}

// =========================================================================
// Vendor profile completion (sell-app dashboard checklist + progress bar)
// =========================================================================
//
// Thin wrapper around `shop.rpc_vendor_profile_completion` (added in
// `20260601004100_shop_vendor_profile_completion.sql`). Returns a
// derived completion ledger every time -- no per-row state is
// persisted on `shop.vendors`, so unpublishing a product or removing
// a logo immediately decrements the percentage on the next read.
//
// Step labels + CTA paths live in the React layer (i18n + routing);
// the RPC only emits stable keys + `done` booleans so this contract
// stays presentation-agnostic across surfaces (dashboard, profile
// page, future admin "vendor health" view).

/**
 * Canonical step keys emitted by `shop.rpc_vendor_profile_completion`.
 * Keep this in sync with the migration if a new step is added.
 */
export type VendorProfileCompletionStepKey =
  | 'company_name'
  | 'tagline'
  | 'about'
  | 'logo'
  | 'banner'
  | 'website'
  | 'support_email'
  | 'support_phone'
  | 'support_whatsapp'
  | 'inventory_country'
  | 'ships_to'
  | 'payment_methods'
  | 'first_product'
  | 'public_storefront';

export interface VendorProfileCompletionStep {
  key: VendorProfileCompletionStepKey;
  /** True when the underlying field / row exists. */
  done: boolean;
  /**
   * True when this step is part of the bare-minimum-to-operate set
   * (`company_name`, `logo`, `support_email`, `inventory_country`,
   * `ships_to`, `payment_methods`, `first_product`,
   * `public_storefront`). Required steps cannot be skipped.
   */
  required: boolean;
  /**
   * True when the vendor can dismiss this step from the checklist
   * (`tagline`, `banner`, `about`, `website`, `support_phone`,
   * `support_whatsapp`). Always the inverse of `required` in the
   * current implementation, but surfaced separately so future
   * "required-but-skippable for some vendors" semantics don't break
   * the contract.
   */
  skippable: boolean;
  /**
   * True when the vendor has dismissed this step via
   * `shop.rpc_vendor_profile_skip_step`. Skipped steps drop out of
   * BOTH the numerator and the denominator of `completion_pct`.
   */
  skipped: boolean;
}

export interface VendorProfileCompletion {
  vendor_id: string;
  computed_at: string;
  /** Every step the system knows about (required + skippable). */
  total_steps: number;
  /** Steps in the required set (never skippable). */
  required_steps: number;
  /** Steps in the skippable set (the optional / nice-to-have items). */
  skippable_steps: number;
  /** Skippable steps the vendor has currently dismissed. */
  skipped_steps: number;
  /**
   * `total_steps - skipped_steps`. The denominator of
   * `completion_pct`. Surfaced so callers can render "X of Y steps
   * complete" with the dismissed items already discounted.
   */
  active_steps: number;
  /** Done AND not skipped. The numerator of `completion_pct`. */
  completed_steps: number;
  /**
   * 0..100, rounded server-side. Equals
   * `round(completed_steps / active_steps * 100)` so dismissing an
   * N/A step raises the percentage instead of penalizing the vendor.
   */
  completion_pct: number;
  /** Always in canonical order so callers can map labels by index OR key. */
  steps: VendorProfileCompletionStep[];
}

/**
 * Step keys the vendor is allowed to dismiss. Pinned to a fixed set
 * so the server-side CHECK constraint
 * (`vendor_profile_completion_skips_step_key_check`) and the React
 * "Skip" button stay in lockstep. Adding a new skippable step
 * requires updating BOTH the CHECK constraint and this list.
 */
export const SKIPPABLE_PROFILE_COMPLETION_STEP_KEYS = [
  'tagline',
  'banner',
  'about',
  'website',
  'support_phone',
  'support_whatsapp',
] as const satisfies readonly VendorProfileCompletionStepKey[];

export type SkippableVendorProfileCompletionStepKey =
  (typeof SKIPPABLE_PROFILE_COMPLETION_STEP_KEYS)[number];

/**
 * Type guard for runtime narrowing (e.g. in the UI, before calling
 * `skipVendorProfileStep`). Cheaper than re-checking the CHECK
 * constraint on the server for an obvious type mismatch.
 */
export function isSkippableVendorProfileCompletionStepKey(
  key: VendorProfileCompletionStepKey,
): key is SkippableVendorProfileCompletionStepKey {
  return (SKIPPABLE_PROFILE_COMPLETION_STEP_KEYS as readonly string[]).includes(key);
}

/**
 * Best-effort coercion of the RPC's jsonb shape into our typed
 * object. The SQL function always returns every key (and the steps
 * array in canonical order); the defaults here exist so a downstream
 * partial-schema deployment can't crash the dashboard card.
 *
 * The v1 RPC (`20260601004100`) emitted only `key` + `done` per step;
 * the v2 RPC (`20260601004200`) adds `required` + `skippable` +
 * `skipped`. This normalizer accepts both shapes and synthesizes
 * sensible defaults for the v2-only fields so an old client hitting
 * a new server (or vice versa) still renders.
 */
export function normalizeVendorProfileCompletion(
  raw: unknown,
): VendorProfileCompletion {
  const r = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const stepsRaw = Array.isArray(r.steps) ? r.steps : [];
  const steps: VendorProfileCompletionStep[] = stepsRaw
    .map((s) => {
      const e = (s && typeof s === 'object' ? s : {}) as Record<string, unknown>;
      const key = typeof e.key === 'string' ? (e.key as VendorProfileCompletionStepKey) : null;
      if (!key) return null;
      const skippable =
        typeof e.skippable === 'boolean'
          ? e.skippable
          : isSkippableVendorProfileCompletionStepKey(key);
      const required =
        typeof e.required === 'boolean' ? e.required : !skippable;
      return {
        key,
        done: Boolean(e.done),
        required,
        skippable,
        skipped: Boolean(e.skipped),
      } satisfies VendorProfileCompletionStep;
    })
    .filter((s): s is VendorProfileCompletionStep => s !== null);

  const total =
    typeof r.total_steps === 'number' && Number.isFinite(r.total_steps)
      ? r.total_steps
      : steps.length;
  const skipped =
    typeof r.skipped_steps === 'number' && Number.isFinite(r.skipped_steps)
      ? r.skipped_steps
      : steps.filter((s) => s.skipped).length;
  const required =
    typeof r.required_steps === 'number' && Number.isFinite(r.required_steps)
      ? r.required_steps
      : steps.filter((s) => s.required).length;
  const skippable =
    typeof r.skippable_steps === 'number' && Number.isFinite(r.skippable_steps)
      ? r.skippable_steps
      : steps.filter((s) => s.skippable).length;
  const active =
    typeof r.active_steps === 'number' && Number.isFinite(r.active_steps)
      ? r.active_steps
      : total - skipped;
  const done =
    typeof r.completed_steps === 'number' && Number.isFinite(r.completed_steps)
      ? r.completed_steps
      : steps.filter((s) => s.done && !s.skipped).length;
  const pct =
    typeof r.completion_pct === 'number' && Number.isFinite(r.completion_pct)
      ? r.completion_pct
      : active <= 0
        ? 0
        : Math.round((done / active) * 100);

  return {
    vendor_id: typeof r.vendor_id === 'string' ? r.vendor_id : '',
    computed_at:
      typeof r.computed_at === 'string' ? r.computed_at : new Date().toISOString(),
    total_steps: total,
    required_steps: required,
    skippable_steps: skippable,
    skipped_steps: skipped,
    active_steps: active,
    completed_steps: done,
    completion_pct: pct,
    steps,
  };
}

/**
 * Live one-shot fetch of the vendor profile completion ledger.
 * Backed by `shop.rpc_vendor_profile_completion`; caller must be a
 * member of the vendor's team or a platform admin (server enforced).
 */
export async function getVendorProfileCompletion(
  vendorId: string,
): Promise<VendorProfileCompletion> {
  const { data, error } = await (supabase() as any)
    .schema('shop')
    .rpc('rpc_vendor_profile_completion', { p_vendor_id: vendorId });
  if (error) throw error;
  return normalizeVendorProfileCompletion(data);
}

/**
 * Mark a non-required profile-completion step as skipped for the
 * vendor. Idempotent upsert keyed on (vendor_id, step_key); a second
 * call with the same step refreshes `skipped_at` + `reason` instead
 * of failing.
 *
 * The server enforces:
 *   * Caller must be a member of the vendor's team or a platform
 *     admin (`42501` otherwise).
 *   * Step key must be in the skippable allow-list (`22023` otherwise).
 *   * Optional `reason` is trimmed to 1..280 chars when supplied.
 */
export async function skipVendorProfileStep(input: {
  vendorId: string;
  stepKey: SkippableVendorProfileCompletionStepKey;
  reason?: string | null;
}): Promise<void> {
  const { error } = await (supabase() as any)
    .schema('shop')
    .rpc('rpc_vendor_profile_skip_step', {
      p_vendor_id: input.vendorId,
      p_step_key: input.stepKey,
      p_reason: input.reason ?? null,
    });
  if (error) throw error;
}

/**
 * Un-skip a previously-skipped profile-completion step. Idempotent;
 * no-op when the row does not exist. Same authorization gate as
 * `skipVendorProfileStep`.
 */
export async function unskipVendorProfileStep(input: {
  vendorId: string;
  stepKey: VendorProfileCompletionStepKey;
}): Promise<void> {
  const { error } = await (supabase() as any)
    .schema('shop')
    .rpc('rpc_vendor_profile_unskip_step', {
      p_vendor_id: input.vendorId,
      p_step_key: input.stepKey,
    });
  if (error) throw error;
}
